import { html, nothing, type PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { BasePage } from '@vitesse/base-ui/components/base-page';
import type { IntegrationManager } from '../managers/integration-manager';
import type { Integration } from '../../core/domain/integration';
import type { PropertiesView } from '../../core/domain/controller';
import type { BackendClient } from '../bridge/backend-client';
import { statusBadge, statusColor, statusText, type PillTab } from '@vitesse/base-ui/components/templates';
import { button } from '@vitesse/base-ui/components/base-buttons';
import { getSession, requirementFor } from '@vitesse/base-ui';
import { t, formatNumber } from '@vitesse/base-ui/i18n';
import { Integrations } from '@vitesse/proto/vitesse/v1/integrations_pb.js';
import { IntegrationIndex } from '@vitesse/proto/vitesse/v1/symbol_index_pb.js';
import '@vitesse/base-ui/elements/vitesse-gate-overlay';
import '@vitesse/base-ui/elements/vitesse-gated';
import '@vitesse/base-ui/elements/vitesse-editable-description';
import '../components/integration/integration-logs-section';
import '../components/integration/topic-viewport';
import '../components/integration/table-viewport';
import '../components/integration/endpoint-viewport';
import '../components/properties-view';
import '@vitesse/base-ui/elements/vitesse-confirm-dialog';
import './vitesse-add-integration-dialog';

/* The integration detail sub-views — the 2nd integration API's analog of the
 * controller (symbol) page: Paths (the discovered "symbols" — tables, topics or
 * operations, one name across all three archetypes) · Properties (the
 * connection) · Logs (the IP-safe status/lifecycle tail, the SAME <log-tree> the
 * controller/model Logs tabs use). No Program (integrations have none). */
type IntegrationTab = 'paths' | 'properties' | 'logs';

/*
 * <vitesse-integration-detail> — the integration detail page. LIGHT DOM; extends
 * BasePage so it shares the exact back·title·badges·tabs chrome every detail page
 * uses. The header info comes from the roster (IntegrationManager); the Paths tab
 * routes on the integration's VIEW KIND (the single backend classifier):
 *   • 'topic'    → the live-value tree           (<vitesse-topic-viewport>)
 *   • 'table'    → the navigator + data grid      (<vitesse-table-viewport>)
 *   • 'endpoint' → the flat operations value list (<vitesse-endpoint-viewport>)
 * Properties shows the non-secret connection fields. Mirrors <vitesse-model-detail>
 * / the symbol page.
 */
@customElement('vitesse-integration-detail')
export class IntegrationDetail extends BasePage {
  @property({ attribute: false }) backend?: BackendClient;
  @property({ attribute: false }) integrations?: IntegrationManager;
  @property() integrationId = '';
  @property() gatewayId = '';
  @property() gatewayName = '';

  @state() private integration?: Integration;
  /** Item count from the active viewport. The topic tree + endpoint op-list emit it
   *  via count-changed (their schema is materialized); the table viewport is
   *  browse-on-demand and materializes nothing, so it stays 0 there. */
  @state() private gridCount = 0;
  @state() private tab: IntegrationTab = 'paths';
  @state() private confirmOpen = false;
  @state() private deleting = false;
  @state() private deleteError = '';
  @state() private editOpen = false;

  private readonly onChange = (): void => {
    this.integration = this.integrations?.all.find((i) => i.id === this.integrationId);
  };

  /** view_kind 'topic' → the live-value tree viewport (IntegrationIndex / SymbolGrid):
   *  brokers/streams, OPC-UA — anything whose leaves are live values (in-house FTS). */
  private get isTopicView(): boolean {
    return this.integration?.viewKind === 'topic';
  }

  /** view_kind 'table' → the navigator + data-grid viewport: queryable stores
   *  (SQL/doc/wide-column/…) browsed + read on demand, search delegated to the source. */
  private get isTableView(): boolean {
    return this.integration?.viewKind === 'table';
  }

  /** view_kind 'endpoint' → the request-builder viewport: REST/APIs — pick a named
   *  operation, supply inputs, view the flattened response. */
  private get isEndpointView(): boolean {
    return this.integration?.viewKind === 'endpoint';
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.integrations?.addEventListener('change', this.onChange);
    this.onChange();
  }
  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.integrations?.removeEventListener('change', this.onChange);
  }

  // COLD RELOAD (the per-gateway manager arrives a tick after the route resolves)
  // + integration→integration nav (Lit reuses this element, only the id changes):
  // re-wire the roster listener on either. Each viewport (value tree / dataset
  // navigator) owns its own stream lifecycle, keyed on the integrationId prop it
  // receives — nothing to open or tear down here.
  protected willUpdate(changed: PropertyValues): void {
    if (changed.has('integrations')) {
      (changed.get('integrations') as IntegrationManager | undefined)?.removeEventListener('change', this.onChange);
      this.integrations?.addEventListener('change', this.onChange);
      this.onChange();
    }
    if (changed.has('integrationId')) {
      // Lit REUSES this element across integrations (fixed template position; only
      // .integrationId changes). gridCount is only ever set by the topic/endpoint
      // viewports' count-changed — the table viewport emits none — so without this
      // reset a stale count bleeds onto the next integration (e.g. a topic's 347
      // shown on a table's header badge + Paths tab). The new viewport re-emits its
      // own count on mount, or it correctly stays 0 for the browse-on-demand table.
      this.gridCount = 0;
    }
    if (changed.has('integrationId') || changed.has('backend') || changed.has('gatewayId')) {
      this.onChange();
    }
  }

  // ── BasePage hooks ─────────────────────────────────────────────────────────
  protected pageTitle(): string {
    return this.integration?.displayName || this.integrationId;
  }
  protected titleIcon(): string {
    return 'database-arrow-up';
  }
  protected backLabel(): string {
    return this.gatewayName || t('integration.back');
  }
  protected backAriaLabel(): string {
    return t('integration.ariaBack');
  }
  protected onBack(): void {
    this.dispatchEvent(new CustomEvent('navigate', { detail: { view: 'gateway' }, bubbles: true, composed: true }));
  }
  protected renderDescription() {
    const i = this.integration;
    if (!i) return nothing;
    // Editable when the viewer may apply (the write path) and the backend is
    // present. A description edit is a field-masked apply: it never re-enters
    // secrets or disturbs the connection.
    const canEdit = !!this.backend && getSession()?.allowsRpc(Integrations.method.apply) !== false;
    return html`<vitesse-editable-description
      .value=${i.description}
      placeholder=${t('dashboards.descriptionFallback')}
      hint=${t('dashboards.descriptionEditHint')}
      .canEdit=${canEdit}
      .commit=${(next: string) => this.commitDescription(next)}
    ></vitesse-editable-description>`;
  }

  private commitDescription(next: string): void {
    const i = this.integration;
    if (!i || !this.backend) return;
    // Field-masked apply: ONLY the description travels; the service overlays it
    // onto the descriptor it holds — secrets, forwards and targets untouched, no
    // reconnect. `type` rides along because the service validates it before the
    // mask fast-path; everything else under the mask is ignored.
    void this.backend.api
      .applyIntegration(this.gatewayId, {
        integrationId: i.id,
        type: i.type,
        displayName: i.displayName,
        fields: {},
        secrets: {},
        description: next,
        updateMask: ['description'],
      })
      .catch(() => {
        /* transient write failure — the live stream stays the source of truth */
      });
  }

  protected renderBadges() {
    const i = this.integration;
    if (!i) return nothing;
    // Status (lifecycle) is the only state badge; the type is NOT a badge.
    const itemCount = this.gridCount;
    return html`${statusBadge(statusColor(i.status), statusText(i.status))}${
      itemCount > 0 ? statusBadge('blue', t('integration.itemsCount', { n: formatNumber(itemCount) })) : nothing
    }`;
  }
  // Header actions — Edit (re-open the catalog dialog pre-filled) + Delete, the
  // same affordances a model detail header carries, each locked on the procedure
  // it ends in. Edit is gated on the button that OPENS the dialog, not the dialog
  // (the gateway page's add-buttons do the same): a form you cannot submit is a
  // worse refusal than no form. Delete used to be a red button that failed on
  // confirm for a caller without the grant.
  protected renderActions() {
    if (!this.integration) return nothing;
    return html`<vitesse-gated .rpc=${Integrations.method.apply} inline
        >${button({ label: t('integration.edit'), icon: 'square-pen', color: 'blue', onClick: () => (this.editOpen = true) })}</vitesse-gated
      ><vitesse-gated .rpc=${Integrations.method.delete} inline
        >${button({ label: t('integration.delete'), icon: 'trash-2', color: 'red', onClick: () => (this.confirmOpen = true) })}</vitesse-gated
      >`;
  }
  // AI hints follow the active tab (same shape as controller/model detail).
  protected aiHints(): string[] {
    switch (this.tab) {
      case 'logs':
        return [t('integration.hintLogsRecent'), t('integration.hintLogsErrors')];
      case 'properties':
        return [t('integration.hintPropsConfig'), t('integration.hintPropsConnects')];
      case 'paths':
      default:
        return [t('integration.hintPathsExpose'), t('integration.hintPathsFind')];
    }
  }

  private async confirmDelete(): Promise<void> {
    const i = this.integration;
    if (!i || !this.backend || this.deleting) return;
    this.deleting = true;
    this.deleteError = '';
    try {
      const res = await this.backend.api.deleteIntegration(this.gatewayId, i.id);
      if (res.ok) {
        this.confirmOpen = false;
        this.dispatchEvent(new CustomEvent('navigate', { detail: { view: 'gateway' }, bubbles: true, composed: true }));
      } else {
        this.deleteError = res.error || t('integration.deleteFailed');
      }
    } catch (err) {
      this.deleteError = err instanceof Error ? err.message : String(err);
    } finally {
      this.deleting = false;
    }
  }
  protected tabs(): PillTab[] {
    return [
      { id: 'paths', label: t('integration.tabPaths'), icon: 'list-tree', count: this.gridCount > 0 ? this.gridCount : undefined },
      { id: 'properties', label: t('integration.tabProperties'), icon: 'settings' },
      { id: 'logs', label: t('integration.tabLogs'), icon: 'scroll-text' },
    ];
  }
  protected activeTabId(): string {
    return this.tab;
  }
  protected onTabChange(id: string): void {
    if (id !== this.tab) this.tab = id as IntegrationTab;
  }

  protected renderContent() {
    // Logs mount EAGERLY and stay mounted across tab switches (hidden when
    // inactive) so the recent-window replay runs ONCE on page open, in the
    // background — switching to the Logs tab is then instant instead of paying
    // the replay round-trip on every visit (the snappy-preload principle). The
    // shared <vitesse-log-tree> is untouched (no divergence from controller /
    // model logs); only WHEN it mounts changes. Wrapped in a plain <div hidden>
    // so display:none is reliable regardless of the custom element's own display.
    const context = `gateways/${this.gatewayId}/integrations/${this.integrationId}`;
    // Every tab stays in the bar; what is gated is its CONTENT, behind the
    // overlay — the precedent every other detail page sets (a removed tab
    // teaches nothing; a locked one says why). Each overlay names the procedure
    // its content actually calls, so the lock and the refusal read one annotation.
    //
    // Properties is the exception that must spell its requirement out: the fields
    // arrive on Integrations.Subscribe (the descriptor permission) but the tab is
    // ALSO a telemetry-plan surface, and no single RPC carries both. The
    // permission is still read off the Subscribe annotation rather than restated
    // as a string — a second copy of the policy is a copy that drifts.
    const viewDescriptor = requirementFor(Integrations.method.subscribe);
    return html`
      ${INT_DETAIL_STYLE}
      ${this.tab === 'properties'
        ? html`<vitesse-gate-overlay
            .permission=${viewDescriptor?.permission}
            capability="telemetry"
            level="flows"
            context=${context}
            .content=${this.renderProperties()}
          ></vitesse-gate-overlay>`
        : nothing}
      ${this.tab === 'paths'
        ? html`<vitesse-gate-overlay
            class="int-paths-gate"
            .rpc=${this.isTableView ? Integrations.method.browseSchema : IntegrationIndex.method.subscribe}
            context=${context}
            .content=${this.renderPaths()}
          ></vitesse-gate-overlay>`
        : nothing}
      <div ?hidden=${this.tab !== 'logs'}>
        <vitesse-gate-overlay
          .rpc=${Integrations.method.subscribeLogs}
          context=${`gateways/${this.gatewayId}/integrations/${this.integrationId}`}
          .content=${html`<vitesse-integration-logs
            .backend=${this.backend}
            .gatewayId=${this.gatewayId}
            .integrationId=${this.integrationId}
          ></vitesse-integration-logs>`}
        ></vitesse-gate-overlay>
      </div>
      ${this.renderOverlays()}`;
  }

  /** The Paths viewport, routed on the integration's view kind. The table
   *  viewport pages the connector through BrowseSchema; the topic and endpoint
   *  viewports read the materialised IntegrationIndex — both `integration:browse`,
   *  and the overlay above names whichever this content will call. Live VALUES
   *  are a second permission (integration:read) that each viewport decides on
   *  its own: a caller with browse alone gets the catalogue with values absent,
   *  rather than a tab that errors. See integration-access.ts. */
  private renderPaths() {
    if (this.isTopicView) {
      return html`<div class="int-topic-paths">
        <vitesse-topic-viewport
          .backend=${this.backend}
          .gatewayId=${this.gatewayId}
          .integrationId=${this.integrationId}
          .integration=${this.integration}
          @count-changed=${(e: CustomEvent<{ count: number }>) => (this.gridCount = e.detail.count)}
        ></vitesse-topic-viewport>
      </div>`;
    }
    if (this.isTableView) {
      return html`<vitesse-table-viewport
        .backend=${this.backend}
        .gatewayId=${this.gatewayId}
        .integrationId=${this.integrationId}
      ></vitesse-table-viewport>`;
    }
    if (this.isEndpointView) {
      return html`<vitesse-endpoint-viewport
        .backend=${this.backend}
        .gatewayId=${this.gatewayId}
        .integrationId=${this.integrationId}
        .integration=${this.integration}
        @count-changed=${(e: CustomEvent<{ count: number }>) => (this.gridCount = e.detail.count)}
      ></vitesse-endpoint-viewport>`;
    }
    return html`<div class="int-loading">${t('integration.loading')}</div>`;
  }

  // Delete-confirm + Edit dialogs — both native top-layer, so they render above
  // any tab and persist across tab switches (renderContent always includes them).
  private renderOverlays() {
    const i = this.integration;
    return html`${
      this.confirmOpen && i
        ? html`<vitesse-confirm-dialog
            .open=${true}
            heading=${t('integration.deleteHeading')}
            message=${t('integration.deleteMessage', { name: i.displayName })}
            detail=${t('integration.deleteDetail')}
            confirm-label=${t('integration.deleteConfirm')}
            danger
            ?busy=${this.deleting}
            error=${this.deleteError}
            @confirm=${() => void this.confirmDelete()}
            @dialog-close=${() => (this.confirmOpen = false)}
          ></vitesse-confirm-dialog>`
        : nothing
    }${
      this.editOpen
        ? html`<vitesse-add-integration-dialog
            .open=${true}
            .gatewayId=${this.gatewayId}
            .backend=${this.backend}
            .editIntegration=${i}
            @dialog-close=${() => (this.editOpen = false)}
          ></vitesse-add-integration-dialog>`
        : nothing
    }`;
  }

  // Reuses the SAME backend PropertiesView structure + the shared
  // <vitesse-properties-view> as the controller Properties tab (pixel-identical).
  // The integration's connection fields are the key→value rows; NO lifecycle
  // ("Connected" is a status badge, not a property) and NO type row.
  private renderProperties() {
    const view: PropertiesView = {
      tables: [
        {
          title: t('integration.propsConnection'),
          columns: [],
          rows: Object.entries(this.integration?.fields ?? {})
            .filter(([, v]) => v !== '')
            .map(([k, v]) => [k, v]),
        },
      ],
    };
    return html`<vitesse-properties-view .view=${view}></vitesse-properties-view>`;
  }
}

// The transient placeholder shown only while the catalog (and thus the leaf kind
// that selects the viewport) hasn't arrived yet — sub-frame in practice. Light-DOM
// page, so an int- prefixed class keeps the scoped <style> collision-free.
const INT_DETAIL_STYLE = html`<style>
  .int-loading {
    padding: 24px;
    color: var(--text-secondary);
    font-size: 13px;
  }
  /* The Paths overlay sits between the page's flex column and the viewport. The
     component is display:block by default, which would cut the flex chain the
     topic grid measures its height through — so on this page it is itself a
     flex column that passes the remaining height down. When refused it holds
     the skeleton instead, which needs nothing of the chain. */
  vitesse-gate-overlay.int-paths-gate {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }
  /* TOPIC paths wrapper: a flex column that hands the SymbolGrid host (flex:1) a
     bounded remaining-height box. base-page-content is itself a flex column. */
  .int-topic-paths {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }
  .int-topic-paths .vg-host {
    flex: 1;
    min-height: 0;
  }
  /* The topic viewport's toolbar. It lives here rather than in the component
     because that component renders in LIGHT DOM (it shares symbolGrid.scss and
     this flex layout), so a static styles block there would be inert. Being
     light DOM is also why its buttons pick up the global base-button styles with
     no shadow-root workaround. */
  .int-topic-paths .int-bar {
    flex: none;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 0 8px;
    flex-wrap: wrap;
  }
  .int-topic-paths .int-bar .scope {
    font-size: 12px;
    color: var(--text-secondary);
    font-family: 'SF Mono', ui-monospace, Menlo, monospace;
    border: 1px solid var(--border-color);
    border-radius: 5px;
    padding: 2px 7px;
  }
  .int-topic-paths .int-bar .count {
    font-size: 12px;
    color: var(--text-secondary);
  }
  .int-topic-paths .int-bar .err {
    font-size: 12px;
    color: var(--red, #d64541);
  }
  /* The topic viewport's toolbar. It lives here rather than in the component
     because that component renders in LIGHT DOM (it shares symbolGrid.scss and
     this flex layout), so a static styles block there would be inert. Being
     light DOM is also why its buttons pick up the global base-button styles with
     no shadow-root workaround. */
  .int-topic-paths .int-bar {
    flex: none;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 0 8px;
    flex-wrap: wrap;
  }
  .int-topic-paths .int-bar .scope {
    font-size: 12px;
    color: var(--text-secondary);
    font-family: 'SF Mono', ui-monospace, Menlo, monospace;
    border: 1px solid var(--border-color);
    border-radius: 5px;
    padding: 2px 7px;
  }
  .int-topic-paths .int-bar .count {
    font-size: 12px;
    color: var(--text-secondary);
  }
  .int-topic-paths .int-bar .err {
    font-size: 12px;
    color: var(--red, #d64541);
  }
</style>`;

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-integration-detail': IntegrationDetail;
  }
}
